<template>
  <div class="when-step min-h-full" :class="{ 'opacity-90': readOnlyConfig?.isReadOnly }">
    <div class="flex items-center gap-2 mb-4">
      <h2 class="text-lg font-medium text-gray-900">WHEN - Quand l'activité à-t-elle lieu ?</h2>
      <BookCheckIcon v-show="isStepValid" class="w-5 h-5 text-green-500" />
    </div>

    <div class="space-y-6">
      <!-- Type de sélection -->
      <div v-if="!readOnlyConfig?.isReadOnly" class="flex gap-4">
        <div :class="[
          'flex-1 py-3 px-4 rounded-lg cursor-pointer text-center transition-colors duration-200',
          dateType === 'single'
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        ]" @click="setDateType('single')">
          Date unique
        </div>
        <div :class="[
          'flex-1 py-3 px-4 rounded-lg cursor-pointer text-center transition-colors duration-200',
          dateType === 'range'
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        ]" @click="setDateType('range')">
          Période
        </div>
      </div>

      <!-- Affichage readonly du type de date -->
      <div v-if="readOnlyConfig?.isReadOnly && selectedDate" class="text-center">
        <span class="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg">
          {{ dateType === 'single' ? 'Date unique' : 'Période' }}
        </span>
      </div>

      <!-- Bouton Maintenant -->
      <div v-if="!readOnlyConfig?.isReadOnly" class="flex justify-center">
        <button
          class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
          @click="setNow">
          <ClockIcon class="w-4 h-4 mr-2" />
          Maintenant
        </button>
      </div>

      <!-- Sélecteur de date -->
      <div class="relative z-50">
        <VueDatePicker v-if="!readOnlyConfig?.isReadOnly" v-model="selectedDate" :range="dateType === 'range'"
          :enable-time-picker="showTimePicker" :timezone="selectedTimezone?.tzCode" :format="dateDisplayFormat"
          :placeholder="dateType === 'range' ? 'Sélectionnez une période' : 'Sélectionnez une date'" :auto-apply="true"
          :enable-seconds="false" :is-24="true" locale="fr-FR" class="date-picker-custom">
          <template #trigger>
            <div class="relative w-full">
              <div
                class="w-full p-4 border rounded-lg bg-white hover:bg-gray-50 cursor-pointer transition-colors duration-200 text-gray-700">
                {{ formatDisplayDate || (dateType === 'range' ? 'Sélectionnez une période' : 'Sélectionnez une date') }}
              </div>
              <button v-if="selectedDate && !readOnlyConfig?.isReadOnly"
                class="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-red-500 hover:text-red-700"
                @click.stop="clearDateValue">
                <XIcon class="h-4 w-4" />
              </button>
            </div>
          </template>
        </VueDatePicker>

        <!-- Affichage readonly de la date -->
        <div v-if="readOnlyConfig?.isReadOnly" class="w-full p-4 border rounded-lg bg-gray-50 text-gray-700">
          {{ formatDisplayDate || 'Aucune date sélectionnée' }}
        </div>
      </div>

      <!-- Option heure -->
      <div v-if="!readOnlyConfig?.isReadOnly" class="flex justify-center">
        <div :class="[
          'inline-flex items-center px-4 py-2 rounded-md cursor-pointer transition-colors duration-200',
          showTimePicker
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        ]" @click="toggleTimePicker">
          <ClockIcon class="w-4 h-4 mr-2" />
          Inclure l'heure
        </div>
      </div>

      <!-- Affichage readonly de l'option heure -->
      <div v-if="readOnlyConfig?.isReadOnly && showTimePicker" class="flex justify-center">
        <span class="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md">
          <ClockIcon class="w-4 h-4 mr-2" />
          Heure incluse
        </span>
      </div>

      <!-- Fuseau horaire -->
      <div v-if="showTimePicker && !readOnlyConfig?.isReadOnly" class="flex items-center justify-center gap-4">
        <label class="text-sm font-medium text-gray-700 whitespace-nowrap">
          Fuseau horaire :
        </label>
        <select v-model="selectedTimezone" class="px-3 py-2 border rounded-md bg-white max-w-[300px]">
          <option v-for="tz in timezones" :key="tz.tzCode" :value="tz">
            {{ tz.label }}
          </option>
        </select>
      </div>

      <!-- Affichage readonly du fuseau horaire -->
      <div v-if="showTimePicker && readOnlyConfig?.isReadOnly && selectedTimezone"
        class="flex items-center justify-center gap-4">
        <label class="text-sm font-medium text-gray-700 whitespace-nowrap">
          Fuseau horaire :
        </label>
        <span class="px-3 py-2 bg-gray-50 border rounded-md text-gray-700 max-w-[300px]">
          {{ selectedTimezone.label }}
        </span>
      </div>

      <!-- Lien temporel -->
      <div v-if="!readOnlyConfig?.isReadOnly" class="flex items-center justify-center gap-4">
        <label class="text-sm font-medium text-gray-700 whitespace-nowrap">
          Lien temporel :
        </label>
        <input v-model="timeLink" type="text" placeholder="https://..."
          class="px-3 py-2 border rounded-md bg-white max-w-[300px]" />
      </div>

      <!-- Affichage readonly du lien temporel -->
      <div v-if="readOnlyConfig?.isReadOnly && timeLink"
        class="flex items-center justify-center gap-4">
        <label class="text-sm font-medium text-gray-700 whitespace-nowrap">
          Lien temporel :
        </label>
        <span class="px-3 py-2 bg-gray-50 border rounded-md text-gray-700 max-w-[300px]">
          {{ timeLink }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ReadOnlyConfig } from "@/types/readOnly";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { BookCheckIcon, ClockIcon, XIcon } from 'lucide-vue-next';
import timezones from 'timezones-list';
import { computed, onMounted, ref, watch } from 'vue';

interface Timezone {
  label: string;
  tzCode: string;
  name: string;
  utc: string;
}

interface Props {
  modelValue: {
    start_time: string | null;
    end_time: string | null;
    time_zone: string | null;
    time_link?: string;
  };
  readOnlyConfig?: ReadOnlyConfig;
}

const props = withDefaults(defineProps<Props>(), {
  readOnlyConfig: () => ({ isReadOnly: false, allowNavigation: true, showEditHints: true })
});

const emit = defineEmits(['update:model-value', 'can-proceed', 'isValid']);

// État local
const dateType = ref<'single' | 'range'>('single');
const selectedDate = ref<Date | [Date, Date] | null>(null);
const showTimePicker = ref(false);
const selectedTimezone = ref<Timezone | null>(null);
const timeLink = ref<string>('');

// Gestion du changement de type de date
const setDateType = (type: 'single' | 'range'): void => {
  if (props.readOnlyConfig?.isReadOnly) return;
  dateType.value = type;
  selectedDate.value = null;
};

// Format d'affichage en fonction des options
const dateDisplayFormat = computed(() => {
  return showTimePicker.value ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy';
});

// Formattage de l'affichage de la date
const formatDisplayDate = computed((): string => {
  if (!selectedDate.value) return '';

  const formatSingleDate = (date: Date): string => {
    const baseFormat = date.toLocaleDateString('fr-FR');
    if (showTimePicker.value) {
      return `${baseFormat} ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return baseFormat;
  };

  if (dateType.value === 'range' && Array.isArray(selectedDate.value)) {
    const [start, end] = selectedDate.value;
    if (!start || !end) return '';
    return `Du ${formatSingleDate(start)} au ${formatSingleDate(end)}`;
  }

  if (dateType.value === 'single' && selectedDate.value instanceof Date) {
    return formatSingleDate(selectedDate.value);
  }

  return '';
});

// Validation
const isStepValid = computed(() => {
  if (dateType.value === 'single') {
    return selectedDate.value instanceof Date;
  }
  return Array.isArray(selectedDate.value) &&
    selectedDate.value[0] instanceof Date &&
    selectedDate.value[1] instanceof Date;
});

// Mise à jour du modèle
const updateModelValue = (): void => {
  if (props.readOnlyConfig?.isReadOnly) return;

  const modelUpdate = {
    ...props.modelValue,
    time_zone: showTimePicker.value ? selectedTimezone.value?.tzCode || 'Europe/Paris' : null,
    time_link: timeLink.value || undefined
  };

  if (!selectedDate.value) {
    modelUpdate.start_time = null;
    modelUpdate.end_time = null;
  } else if (dateType.value === 'single' && selectedDate.value instanceof Date) {
    modelUpdate.start_time = selectedDate.value.toISOString();
    modelUpdate.end_time = null;
  } else if (Array.isArray(selectedDate.value)) {
    const [start, end] = selectedDate.value;
    if (start && end) {
      modelUpdate.start_time = start.toISOString();
      modelUpdate.end_time = end.toISOString();
    }
  }

  emit('update:model-value', modelUpdate);
};

// Fonction pour réinitialiser la date
const clearDateValue = (e: Event): void => {
  if (props.readOnlyConfig?.isReadOnly) return;
  e.preventDefault();
  selectedDate.value = null;
};

// Fonction pour définir la date à maintenant
const setNow = (): void => {
  if (props.readOnlyConfig?.isReadOnly) return;
  const now = new Date();
  dateType.value = 'single';
  selectedDate.value = now;
  showTimePicker.value = true;
  selectedTimezone.value = timezones.find(tz => tz.tzCode === 'Europe/Paris') || timezones[0];
};

// Toggle de l'affichage de l'heure
const toggleTimePicker = (): void => {
  if (props.readOnlyConfig?.isReadOnly) return;
  showTimePicker.value = !showTimePicker.value;
  if (showTimePicker.value) {
    selectedTimezone.value = timezones.find(tz => tz.tzCode === 'Europe/Paris') || timezones[0];
  } else {
    selectedTimezone.value = null;
  }
};

// Watchers
watch(isStepValid, (newValue: boolean): void => {
  emit('isValid', newValue);
}, { immediate: true });

watch([selectedDate, selectedTimezone, dateType, showTimePicker, timeLink], (): void => {
  updateModelValue();
});

// Initialisation
onMounted((): void => {
  emit('can-proceed', true);

  // Restaurer les valeurs sauvegardées
  if (props.modelValue.start_time) {
    const startDate = new Date(props.modelValue.start_time);

    if (props.modelValue.end_time) {
      const endDate = new Date(props.modelValue.end_time);
      dateType.value = 'range';
      selectedDate.value = [startDate, endDate];
    } else {
      dateType.value = 'single';
      selectedDate.value = startDate;
    }

    // Activer l'affichage de l'heure si une timezone est définie
    if (props.modelValue.time_zone) {
      showTimePicker.value = true;
      const savedTimezone = timezones.find((tz: Timezone): boolean => tz.tzCode === props.modelValue.time_zone);
      if (savedTimezone) {
        selectedTimezone.value = savedTimezone;
      }
    }

    // Restaurer le lien temporel
    if (props.modelValue.time_link) {
      timeLink.value = props.modelValue.time_link;
    }
  }
});
</script>

<style>
.date-picker-custom {
  width: 100%;
}

.date-picker-custom :deep(.dp__input) {
  display: none;
}

.date-picker-custom :deep(.dp__main) {
  width: 100%;
}

.date-picker-custom :deep(.dp__now_default) {
  color: white;
  background-color: rgb(37 99 235);
}

/* Forcer le calendrier à s'afficher au-dessus de tout */
.date-picker-custom :deep(.dp__menu) {
  z-index: 9999 !important;
  position: fixed !important;
}

.date-picker-custom :deep(.dp__overlay) {
  z-index: 9998 !important;
}

/* S'assurer que le conteneur parent ne coupe pas le calendrier */
.when-step {
  position: relative;
  z-index: 1;
}
</style>